import { Box } from '@mui/material';
import Divider from '@mui/material/Divider';
import { useCallback, useMemo, useState } from 'react';

import DropdownV2 from 'components/DropdownComponentV2';
import ModalComponent from '../../../../../components/ModalComponent';

import {
  IRecognitionSimpleProps,
  IRecognitionTypes
} from '../../../../../services/Recognition/types';

import { useTranslation } from 'react-i18next';
import { getRecognitionTypesOptions } from '../../utils';
import CustomerRecognitionEditForm from '../forms/CustomerRecognitionEditForm';
import GeneralRecognitionEditForm from '../forms/GeneralRecognitionEditForm';
import ResponsibleRecognitionEditForm from '../forms/ResponsibleRecognitionEditForm';
import StudentRecognitionEditForm from '../forms/StudentRecognitionEditForm';

interface IRecognitionEditModalProps {
  isOpen: boolean;
  selectedRecognition?: IRecognitionSimpleProps;
  handleSaveForm: () => void;
  handleClose: () => void;
}

export const RecognitionsEditModal = ({
  isOpen,
  handleSaveForm,
  handleClose,
  selectedRecognition,
}: IRecognitionEditModalProps) => {
  const { t } = useTranslation();
  const [selectedRecognitionType, setSelectedRecognitionType] = useState<IRecognitionTypes | undefined>(undefined);
  
  const activeType = (selectedRecognitionType || selectedRecognition?.discriminator) as IRecognitionTypes | undefined;
  const faceOptions = useMemo(() => getRecognitionTypesOptions(), []);

  const formattedData = useMemo(() => {
    if (!selectedRecognition) return undefined;

    const data = { ...selectedRecognition };

    if (data.discriminator === IRecognitionTypes.STUDENT) {
      data.registration = data.registration || data.userId;
    } else {
      data.cpf = data.cpf || data.userId;
    }

    return data;
  }, [selectedRecognition]);

  const onClose = useCallback(() => {
    setSelectedRecognitionType(undefined);
    handleClose();
  }, [handleClose]);

  const onSaveForm = useCallback(() => {
    handleSaveForm();
  }, [handleSaveForm]);

  const recognitionFormContent = useMemo(() => {
    if (!activeType) return null;

    const id = selectedRecognition?.id;

    switch (activeType) {
      case IRecognitionTypes.GENERAL:
        return <GeneralRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      case IRecognitionTypes.CUSTOMER:
        return <CustomerRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      case IRecognitionTypes.STUDENT:
        return <StudentRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      case IRecognitionTypes.RESPONSIBLE:
        return <ResponsibleRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      default:
        return <>{t('recognitionPage.modalEditFace.typesOfResponsible.faceTypeNotFound')}</>;
    }
  }, [activeType, selectedRecognition?.id, formattedData, onSaveForm, t]);

  return (
    <ModalComponent
      title={
        selectedRecognition
          ? t('recognitionPage.modalEditFace.editFace')
          : t('recognitionPage.modalEditFace.registerFace')
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      <DropdownV2
        disabled={!!(selectedRecognition && selectedRecognition.discriminator)}
        label={t('recognitionPage.modalEditFace.typesOfResponsible.faceType')}
        value={activeType ?? null}
        labelField="label"
        valueField="value"
        options={faceOptions}
        onChange={(val) => setSelectedRecognitionType(val ?? undefined)}
      />

      {activeType && (
        <>
          <Divider sx={{ mt: 2 }} variant="middle" color={'#b7b7b7'} />
          <Box key={activeType} sx={{ mt: 4 }}>
            {recognitionFormContent}
          </Box>
        </>
      )}
    </ModalComponent>
  );
};

export default RecognitionsEditModal;
